<script setup lang="ts">
/**
 * Drawer-container for the deposit-requests queue (per
 * `architecture-principles.md §20`).
 *
 * Owns the drawer chrome, the detail query, the two action mutations
 * (approve, reject), the corresponding confirmation dialogs, and the
 * treasury-bank-account picker query that feeds the approve dialog.
 * The queue container only needs to forward the selected row via
 * `selectedRow` and listen for `@close`.
 */
import { computed, ref, watch } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import Avatar from '@uiKit/components/Avatar.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'

import { useAppToast } from '@shared/composables/useAppToast'
import { getIdempotencyKey } from '@shared/utils'

import DepositRequestDetail from '../components/DepositRequestDetail.vue'
import ApproveDepositDialog from '../components/ApproveDepositDialog.vue'
import RejectDepositDialog from '../components/RejectDepositDialog.vue'

import { useDepositRequestDetailQuery } from '../api/queries/useDepositRequestDetailQuery'
import { useApproveDepositRequestMutation } from '../api/mutations/useApproveDepositRequestMutation'
import { useRejectDepositRequestMutation } from '../api/mutations/useRejectDepositRequestMutation'
import { getDepositStatusColor, getDepositStatusLabel } from '../utils/depositRequestStatusUtils'
import { DEPOSIT_REQUEST_STATUS, type DepositRequestRow } from '../types/depositRequestsTypes'

import { TREASURY_BANK_ACCOUNT_STATUS, useTreasuryBankAccountsQuery } from '../../treasuryAccounts'

const props = defineProps<{
  selectedRow: DepositRequestRow | null
}>()

const emit = defineEmits<{
  close: []
}>()

// ─── Drawer state + detail query ────────────────────────────────────────────

const selectedId = computed(() => props.selectedRow?.id ?? '')

const idempotencyKey = ref('')
watch(
  () => props.selectedRow,
  (row) => {
    idempotencyKey.value = row ? getIdempotencyKey() : ''
  },
)

const { data: details, isLoading: detailsLoading } = useDepositRequestDetailQuery(selectedId)

const drawerVisible = computed(() => props.selectedRow !== null)

// The investor-entered UTR. Sourced from the list row because the detail
// endpoint's `proof` omits it (it only carries the auto-generated
// paymentReference). This is the reference operators reconcile against
// and that the admin's approval reference must match.
const investorReference = computed(() => props.selectedRow?.utrNumber ?? null)

function closeDrawer() {
  emit('close')
}

// ─── Display ────────────────────────────────────────────────────────────────

const drawerSubtitle = computed(() => props.selectedRow?.investorEmail ?? '')
const drawerInvestorName = computed(() => {
  if (details.value?.investor) {
    const first = details.value.investor.firstName ?? ''
    const last = details.value.investor.lastName ?? ''
    return `${first} ${last}`.trim() || props.selectedRow?.investorName || ''
  }
  return props.selectedRow?.investorName || ''
})

// ─── Treasury bank account picker (feeds approve dialog) ────────────────────

const treasuryAccountParams = computed(() => ({
  status: TREASURY_BANK_ACCOUNT_STATUS.ACTIVE,
  currencyCode: details.value?.currencyCode ?? '',
}))
const { data: treasuryAccounts, isLoading: treasuryAccountsLoading } =
  useTreasuryBankAccountsQuery(treasuryAccountParams)

// ─── Mutations + dialog state ───────────────────────────────────────────────

const { addToast } = useAppToast()

const approveDialogVisible = ref(false)
const rejectDialogVisible = ref(false)

const {
  mutate: approveMutate,
  isPending: isApproving,
  error: approveError,
  reset: resetApprove,
} = useApproveDepositRequestMutation()
const {
  mutate: rejectMutate,
  isPending: isRejecting,
  error: rejectError,
  reset: resetReject,
} = useRejectDepositRequestMutation()

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

const approveErrorMessage = computed(() => describeError(approveError.value))
const rejectErrorMessage = computed(() => describeError(rejectError.value))

function openApprove() {
  resetApprove()
  approveDialogVisible.value = true
}
function openReject() {
  resetReject()
  rejectDialogVisible.value = true
}

function handleApproveConfirm(payload: {
  treasuryBankAccountId: string
  approvedBankReference: string
}) {
  if (!selectedId.value) return
  approveMutate(
    {
      id: selectedId.value,
      treasuryBankAccountId: payload.treasuryBankAccountId,
      approvedBankReference: payload.approvedBankReference,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Deposit approved',
          message: 'The investor wallet has been credited.',
        })
        approveDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

function handleRejectConfirm(payload: { reason: string }) {
  if (!selectedId.value) return
  rejectMutate(
    {
      id: selectedId.value,
      reason: payload.reason,
      idempotencyKey: idempotencyKey.value,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Deposit rejected',
          message: 'The investor has been notified.',
        })
        rejectDialogVisible.value = false
        closeDrawer()
      },
    },
  )
}

const showActionFooter = computed(
  () => details.value?.status === DEPOSIT_REQUEST_STATUS.PENDING_VERIFICATION,
)
</script>

<template>
  <AppDrawer
    :model-value="drawerVisible"
    position="right"
    width="540px"
    :show-close-icon="false"
    :loading="detailsLoading"
    :dismissable="!isApproving && !isRejecting"
    @update:model-value="(v) => !v && closeDrawer()"
  >
    <template #header>
      <DrawerHeader
        :title="drawerInvestorName"
        :subtitle="drawerSubtitle"
        :disable-close="isApproving || isRejecting"
        @close="closeDrawer"
      >
        <template #avatar>
          <Avatar :id="selectedRow?.investorId ?? ''" :name="drawerInvestorName" />
        </template>
        <template #extras>
          <StatusBadge
            v-if="details"
            :color="getDepositStatusColor(details.status)"
            :label="getDepositStatusLabel(details.status)"
          />
        </template>
      </DrawerHeader>
    </template>

    <Box v-if="details" padding="4">
      <DepositRequestDetail :details="details" :investor-utr="investorReference" />
    </Box>

    <template v-if="details && showActionFooter" #footer>
      <Box class="drawer-footer" flex justify="end" gap="2">
        <Button
          label="Reject"
          color="danger"
          variant="outline"
          :disabled="isApproving"
          @click="openReject"
        />
        <Button label="Approve" color="primary" :disabled="isRejecting" @click="openApprove" />
      </Box>
    </template>
  </AppDrawer>

  <ApproveDepositDialog
    v-if="details"
    :visible="approveDialogVisible"
    :currency-code="details.currencyCode"
    :accounts="treasuryAccounts ?? []"
    :accounts-loading="treasuryAccountsLoading"
    :loading="isApproving"
    :api-error="approveDialogVisible ? approveErrorMessage : null"
    :investor-reference="investorReference"
    @update:visible="approveDialogVisible = $event"
    @confirm="handleApproveConfirm"
  />

  <RejectDepositDialog
    :visible="rejectDialogVisible"
    :loading="isRejecting"
    :api-error="rejectDialogVisible ? rejectErrorMessage : null"
    @update:visible="rejectDialogVisible = $event"
    @confirm="handleRejectConfirm"
  />
</template>

<style scoped>
.drawer-footer {
  width: 100%;
  padding: 0.25rem 0;
}
</style>
